import {
  Alert,
  Box,
  Button,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useState, useEffect, useCallback } from "react";

import { useAdminSender } from "../api";
import { MapCenterTracker } from "./MapCenterTracker";
import { MapBoundsUpdater } from "./MapBoundsUpdater";
import { DefaultIcon, HighlightIcon, DuplicateIcon } from "./MapMarkerIcons";
import { DEFAULT_CENTER } from "../constants/leafletMap";
import { useDuplicateCheck, type PlaceWithDuplicateCheck } from "../hooks/useDuplicateCheck";

interface PlaceSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (placeId: string) => void;
  currentPlaceId?: string;
  currentSpotId?: string;
  initialSearchKeyword?: string;
}

export const PlaceSearchModal = ({
  open,
  onClose,
  onSelect,
  currentPlaceId,
  currentSpotId,
  initialSearchKeyword
}: PlaceSearchModalProps) => {
  const {
    sender: searchPlaces,
    isLoading: isSearching,
    error: searchError
  } = useAdminSender({
    apiName: "spot_manager",
    serviceName: "googlePlaces",
    operationId: "searchPlaces"
  });

  const { checkPlacesBatch } = useDuplicateCheck();

  const [searchKeyword, setSearchKeyword] = useState(initialSearchKeyword || "");
  const [searchResults, setSearchResults] = useState<PlaceWithDuplicateCheck[]>([]);
  const [hoveredPlaceId, setHoveredPlaceId] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lon: number }>({
    lat: DEFAULT_CENTER[0],
    lon: DEFAULT_CENTER[1]
  });

  // 地図の中心座標更新用のコールバック（useCallbackで安定化）
  const handleMapCenterChange = useCallback((lat: number, lon: number) => {
    setMapCenter({ lat, lon });
  }, []);

  // モーダルが開かれたときに初期値を設定
  useEffect(() => {
    if (open && initialSearchKeyword) {
      setSearchKeyword(initialSearchKeyword);
    }
  }, [open, initialSearchKeyword]);

  const handleSearch = async () => {
    if (!searchKeyword.trim()) {
      alert("検索キーワードを入力してください");
      return;
    }

    setSearchResults([]);

    try {
      const result = await searchPlaces({
        requestBody: {
          query: searchKeyword,
          location_bias: {
            lat: mapCenter.lat,
            lon: mapCenter.lon
          }
        }
      });

      if (result && result.places) {
        // 検索結果を表示（重複チェック中）
        const placesWithCheckingFlag = result.places.map((p) => ({
          ...p,
          isDuplicateChecking: true
        }));
        setSearchResults(placesWithCheckingFlag);

        // 重複チェック実行
        const checkedPlaces = await checkPlacesBatch(result.places, currentSpotId);
        setSearchResults(checkedPlaces);
      }
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  const handleSelectPlace = (placeId: string, existingSpotId?: string | null) => {
    // 重複チェック: 他のスポットで既に使用されている場合はエラー
    if (existingSpotId) {
      alert(`このplace_idは既に別のスポット（${existingSpotId}）で使用されています。`);
      return;
    }

    // 現在のPlaceIDと比較
    if (currentPlaceId && placeId === currentPlaceId) {
      alert("place_idは最新です");
    } else {
      alert("place_idを更新しました");
      onSelect(placeId);
    }
    onClose();
  };

  const handleClose = () => {
    setSearchKeyword("");
    setSearchResults([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>地図からPlaceIDを検索</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
          >
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                label="検索キーワード（例: 名古屋 カフェ）"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                required
              />
              <Button
                type="submit"
                variant="contained"
                disabled={isSearching}
                sx={{ minWidth: 120 }}
              >
                {isSearching ? <CircularProgress size={24} /> : "検索"}
              </Button>
            </Stack>
          </form>

          {searchError && <Alert severity="error">検索に失敗しました</Alert>}

          {searchResults.length > 0 && (
            <Typography variant="body2" color="success.main">
              {searchResults.length}件の結果が見つかりました
            </Typography>
          )}

          <Box sx={{ height: 500, width: "100%", mb: 3 }}>
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={13}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapBoundsUpdater searchResults={searchResults} />
              <MapCenterTracker onCenterChange={handleMapCenterChange} />
              {searchResults.map((place) => {
                const isHovered = hoveredPlaceId === place.place_id;
                const markerIcon = isHovered
                  ? HighlightIcon
                  : place.existingSpotId
                    ? DuplicateIcon
                    : DefaultIcon;

                return place.location ? (
                  <Marker
                    key={place.place_id}
                    position={[place.location.lat, place.location.lon]}
                    icon={markerIcon}
                    eventHandlers={{
                      mouseover: () => setHoveredPlaceId(place.place_id),
                      mouseout: () => setHoveredPlaceId(null)
                    }}
                  >
                    <Popup>
                      <div>
                        <strong>{place.name}</strong>
                        <br />
                        <span style={{ fontSize: "0.875rem", color: "#666" }}>
                          {place.address || "住所情報なし"}
                        </span>
                        {place.existingSpotId && (
                          <>
                            <br />
                            <span style={{ fontSize: "0.75rem", color: "#d32f2f" }}>
                              既に使用中: {place.existingSpotId}
                            </span>
                          </>
                        )}
                        <br />
                        <br />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleSelectPlace(place.place_id, place.existingSpotId)}
                          disabled={place.isDuplicateChecking || Boolean(place.existingSpotId)}
                        >
                          {place.isDuplicateChecking
                            ? "確認中..."
                            : place.existingSpotId
                              ? "使用中"
                              : "このPlaceIDを選択"}
                        </Button>
                      </div>
                    </Popup>
                  </Marker>
                ) : null;
              })}
            </MapContainer>
          </Box>

          {searchResults.length === 0 && !isSearching && (
            <Typography variant="body2" color="text.secondary">
              検索キーワードを入力して検索してください
            </Typography>
          )}

          {searchResults.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                検索結果一覧
              </Typography>
              <List sx={{ border: (theme) => `1px solid ${theme.palette.divider}` }}>
                {searchResults.map((place, index) => (
                  <Box
                    key={place.place_id}
                    onMouseEnter={() => setHoveredPlaceId(place.place_id)}
                    onMouseLeave={() => setHoveredPlaceId(null)}
                    onFocus={() => setHoveredPlaceId(place.place_id)}
                    onBlur={() => setHoveredPlaceId(null)}
                    tabIndex={0}
                    sx={{
                      backgroundColor:
                        hoveredPlaceId === place.place_id ? "action.hover" : "transparent"
                    }}
                  >
                    {index > 0 && <Divider />}
                    <ListItem>
                      <Stack
                        width="100%"
                        spacing={1}
                        direction="row"
                        justifyContent="space-between"
                      >
                        <CardContent>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6">{place.name}</Typography>
                            {place.isDuplicateChecking && (
                              <Chip label="確認中..." size="small" color="default" />
                            )}
                            {!place.isDuplicateChecking && place.existingSpotId && (
                              <Chip label="使用中" size="small" color="error" />
                            )}
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {place.address || "住所情報なし"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            place_id: {place.place_id}
                          </Typography>
                          {place.existingSpotId && (
                            <Typography variant="caption" color="error.main" display="block">
                              既に使用されているスポット: {place.existingSpotId}
                            </Typography>
                          )}
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleSelectPlace(place.place_id, place.existingSpotId)}
                            disabled={place.isDuplicateChecking || Boolean(place.existingSpotId)}
                          >
                            {place.isDuplicateChecking
                              ? "確認中..."
                              : place.existingSpotId
                                ? "使用中"
                                : "このPlaceIDを選択"}
                          </Button>
                        </CardActions>
                      </Stack>
                    </ListItem>
                  </Box>
                ))}
              </List>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};
